import type { ClipHttpEnvelope } from "@apis/http/types";
import { isNotNil } from "@kalodata/tools";
import { message } from "antd";
import { t } from "i18next";

export type ErrorMessageOptions = Record<string, string>;

export type ResolvedClipErrorMessage = {
  message: string;
  options?: ErrorMessageOptions;
};
type StorageQuotaErrorData = {
  storageLimit?: string | number;
};
type BackendErrorPayload = Partial<ClipHttpEnvelope<unknown>>;

// 后端未返回 storageLimit 时的兜底显示值，避免 toast 渲染出字面量 `{{storage}}`
const STORAGE_QUOTA_DEFAULT = "500M";

export enum ClipErrorCode {
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",
  KALODATA_UNSUPPORTED_LINK = "KALODATA_UNSUPPORTED_LINK",
  UNSUPPORTED_COUNTRY = "UNSUPPORTED_COUNTRY",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  DISPOSABLE_EMAIL_BLOCKED = "DISPOSABLE_EMAIL_BLOCKED",
  // 这个错误码还未定义错误文案提示，先暂时不处理，用后端的错误提示
  INSUFFICIENT_BENEFIT = "INSUFFICIENT_BENEFIT",
  // 功能不可用，直接使用后端返回的具体 message
  FEATURE_NOT_AVAILABLE = "FEATURE_NOT_AVAILABLE",
  ASSET_STORAGE_QUOTA_EXCEEDED = "ASSET_STORAGE_QUOTA_EXCEEDED",
}

export const ErrorCodeMessage: Record<string, string> = {
  [ClipErrorCode.RATE_LIMIT_EXCEEDED]: "You're moving too fast! Please wait a moment and try again.",
  [ClipErrorCode.UNSUPPORTED_COUNTRY]: "This country is not supported.",
  [ClipErrorCode.INTERNAL_ERROR]: "Something went wrong. Please try again.",
  [ClipErrorCode.DISPOSABLE_EMAIL_BLOCKED]: "This email is not supported. Contact [kaloclip@kalowave.com] for help.",
  [ClipErrorCode.ASSET_STORAGE_QUOTA_EXCEEDED]:
    "Storage full ({{storage}}). Please go to the Library to delete some videos or images, empty the trash, and try again.",
  [ClipErrorCode.FEATURE_NOT_AVAILABLE]:
    "TikTok product links from Germany, France, Italy, and Spain are currently not supported.",
};

const getBackendErrorPayload = (error: unknown): BackendErrorPayload | undefined => {
  const err = error as { response?: { data?: BackendErrorPayload }; data?: BackendErrorPayload } | undefined;
  return err?.response?.data ?? err?.data;
};

export const getClipErrorCode = (error: unknown): string | undefined => {
  const payload = getBackendErrorPayload(error);
  const err = error as { code?: string } | undefined;
  const code = err?.code ?? payload?.code;
  if (code === undefined || code === null || code === "") return undefined;
  return String(code);
};

const getStorageQuotaOptions = (payload?: BackendErrorPayload): ErrorMessageOptions => {
  const data = payload?.data as StorageQuotaErrorData | undefined;
  const storageLimit = data?.storageLimit;
  if (isNotNil(storageLimit) && storageLimit !== "") {
    return { storage: String(storageLimit) };
  }
  return { storage: STORAGE_QUOTA_DEFAULT };
};

const getErrorMessageOptions = (
  errorCode: ClipErrorCode,
  payload?: BackendErrorPayload,
): ErrorMessageOptions | undefined => {
  if (errorCode === ClipErrorCode.ASSET_STORAGE_QUOTA_EXCEEDED) {
    return getStorageQuotaOptions(payload);
  }
  return undefined;
};

export const getErrorMessage = (errorCode: ClipErrorCode) => {
  return ErrorCodeMessage[errorCode];
};

export const getErrorMessageWithOptions = (
  errorCode: ClipErrorCode,
  error?: unknown,
): ResolvedClipErrorMessage | null => {
  const text = getErrorMessage(errorCode);
  if (!text) return null;
  const payload = getBackendErrorPayload(error);

  return {
    message: text,
    options: getErrorMessageOptions(errorCode, payload),
  };
};

export const resolveClipErrorMessage = (error: unknown): ResolvedClipErrorMessage | null => {
  const code = getClipErrorCode(error);
  if (!code) return null;
  return getErrorMessageWithOptions(code as ClipErrorCode, error);
};

export const resolveClipErrorCode = (error: unknown): ClipErrorCode | undefined => {
  const code = getClipErrorCode(error);
  const ClipErrorCodeValues = new Set<string>(Object.values(ClipErrorCode));
  if (!code || !ClipErrorCodeValues.has(code)) return undefined;
  return code as ClipErrorCode;
};

export const processErrorMessage = (error: unknown) => {
  const errorMessage = resolveClipErrorMessage(error);
  if (errorMessage) {
    message.error(t(errorMessage.message, errorMessage.options));
    return true;
  }
  return false;
};
